import { ChangeEvent, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Button from "react-bootstrap/Button";
import { AXNUIContent } from "@iddataweb/idw-common-ui/types";
import { faChevronCircleUp } from "@fortawesome/free-solid-svg-icons/faChevronCircleUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListOl } from "@fortawesome/free-solid-svg-icons/faListOl";
import { DEFAULT_AXNUI_CONTENT } from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { useModalContext } from "@context/use-modal-context";
import { resources } from "@constants/hardcoded-strings";
import { NON_RESTRICTED_ATTRIBUTE_PROPERTIES } from "@constants/non-restricted-attribute-properties";
import { ModalTypes } from "@enums/modal-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { RpServiceAttributePropertyTypes } from "@enums/rp-service-attribute-property-types";
import { AttributeLabel, AttributeType, RpServiceAttributeProperty } from "@idw-types/data/reference-data/generic";
import { SelectOption } from "@idw-types/common";
import { FormattedRpServiceConfigurationAttribute, FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { addObjectsToArrayInCommonReferenceData } from "@redux/actions/common-reference-data-actions";
import { getDefaultAttributeLabel } from "@utils/attribute-label-util";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { AddRpServiceConfigurationAttributeLabelModal } from "@containers/modals/add-rp-service-configuration-attribute-label-modal/add-rp-service-configuration-attribute-label-modal";
import { AttributePropertyRow } from "./attribute-property-row/attribute-property-row";
import styles from "./sortable-attribute-container.less";
import { ClickableIcon } from "@containers/common/clickable-icon/clickable-icon";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";

type SortableAttributeContainerProps = {
    index: number;
    isDisabled: boolean;
    selectedLanguage: LocaleTypes;
    localizeStrings: boolean;
    mergeThemingAndLocalizedFile: boolean;
    attributeLabels: Array<AttributeLabel>;
    canModifyBranding: boolean;
    canViewNonRestrictedAttributeProperties: boolean;
    canModifyNonRestrictedAttributeProperties: boolean;
    canViewAttributeProperties: boolean;
    canModifyAttributeProperties: boolean;
    axnuiContent: AXNUIContent;
    attributePropertyRows: Array<{ properties: Array<RpServiceAttributeProperty> }>;
    attribute: FormattedRpServiceConfigurationAttribute;
    updateAttribute: (attribute: FormattedRpServiceConfigurationAttribute, updatedRpServiceConfigurationProperties: Array<FormattedRpServiceConfigurationProperty>) => void;
    isUsingThemedUI: boolean;
    getUpdatedPropertiesForAttributeLabelUpdate: (
        attributeLabel: AttributeLabel,
        attributeType: AttributeType
    ) => Array<FormattedRpServiceConfigurationProperty>;
    removeAttribute: (attributeToRemove: FormattedRpServiceConfigurationAttribute) => void;
};

export const SortableAttributeContainer = (props: SortableAttributeContainerProps) => {
    const dispatch = useDispatch();
    const modalContext = useModalContext();
    const {
        index,
        isDisabled,
        selectedLanguage,
        localizeStrings,
        mergeThemingAndLocalizedFile,
        attributeLabels,
        axnuiContent,
        canModifyBranding,
        canViewNonRestrictedAttributeProperties,
        canModifyNonRestrictedAttributeProperties,
        canViewAttributeProperties,
        canModifyAttributeProperties,
        attribute,
        attributePropertyRows,
        isUsingThemedUI,
        removeAttribute,
        getUpdatedPropertiesForAttributeLabelUpdate,
        updateAttribute
    } = props;

    const [isExpanded, setIsExpanded] = useState(false);

    const onChangeAttributeLabel = (newValue: SelectOption) => {
        const newAttributeLabel = attribute.attributeLabels.find(al => al.id === newValue.value);
        let properties = null;
        let attributeLabel = newAttributeLabel;

        if (isUsingThemedUI) {
            if (localizeStrings && mergeThemingAndLocalizedFile) {
                attributeLabel = getDefaultAttributeLabel(
                    attribute.attributeType,
                    attribute.attributeLabels,
                    DEFAULT_AXNUI_CONTENT[LocaleTypes.ENGLISH]
                );
            }

            properties = getUpdatedPropertiesForAttributeLabelUpdate(newAttributeLabel, attribute.attributeType);
        }

        updateAttribute({ ...attribute, attributeLabel }, properties);
    };

    const onAttributeLabelCreate = (newAttributeLabel: AttributeLabel) => {
        dispatch(addObjectsToArrayInCommonReferenceData([newAttributeLabel], "axnAttributeLabels"));
        let properties = null;
        let attributeLabel = newAttributeLabel;

        if (isUsingThemedUI) {
            if (localizeStrings && mergeThemingAndLocalizedFile) {
                attributeLabel = getDefaultAttributeLabel(
                    attribute.attributeType,
                    attribute.attributeLabels,
                    DEFAULT_AXNUI_CONTENT[LocaleTypes.ENGLISH]
                );
            }

            properties = getUpdatedPropertiesForAttributeLabelUpdate(newAttributeLabel, attribute.attributeType);
        }

        updateAttribute(
            {
                ...attribute,
                attributeLabels: [...attribute.attributeLabels, newAttributeLabel],
                attributeLabel
            },
            properties
        );
    };

    const onClickAddAttributeLabel = () => {
        const renderAddRpServiceConfigurationAttributeLabelModal = (onModalClose: () => void) => {
            return (
                <AddRpServiceConfigurationAttributeLabelModal
                    attributeType={attribute.attributeType}
                    attributeLabels={attributeLabels}
                    onAttributeLabelCreate={onAttributeLabelCreate}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.AddAttributeLabel,
            renderFunction: renderAddRpServiceConfigurationAttributeLabelModal
        });
    };

    const toggleSetIsExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    const handleCustomTooltipChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateAttribute({
            ...attribute,
            customToolTip: event.target.value
        }, null);
    };

    const updateAttributeProperty = (attributeProperty: RpServiceAttributePropertyTypes, newValue: string) => {
        updateAttribute({
            ...attribute,
            [attributeProperty]: newValue
        }, null);
    };

    const handleRemoveAttribute = () => {
        removeAttribute(attribute);
    }

    const labelForTheming = useMemo(() => {
        if (isUsingThemedUI && localizeStrings && mergeThemingAndLocalizedFile) {
            return axnuiContent.labels.attributes[attribute.attributeType.name];
        } else if (isUsingThemedUI && localizeStrings) {
            return DEFAULT_AXNUI_CONTENT[selectedLanguage].labels.attributes[attribute.attributeType.name];
        }

        return null;
    }, [
        axnuiContent.labels.attributes[attribute.attributeType.name],
        isUsingThemedUI,
        localizeStrings,
        mergeThemingAndLocalizedFile,
        selectedLanguage
    ]);

    const attributeLabelOptions: Array<SelectOption> = useMemo(() => {
        // if we have custom content property label and it doesn't exist in our labels list
        // we need to create a fake dropdown option and add it to the normal list
        if (labelForTheming) {
            const existingAttributeLabel = attribute.attributeLabels.find(attrL => attrL.label === labelForTheming);
            if (!existingAttributeLabel) {
                return [
                    ...attribute.attributeLabels.map(al => ({ label: al.label, value: al.id })),
                    { label: labelForTheming, value: null }
                ];
            }
        }

        // otherwise we just return the normal list
        return attribute.attributeLabels.map(al => ({ label: al.label, value: al.id }));
    }, [
        labelForTheming,
        attribute.attributeLabels,
        axnuiContent.labels.attributes[attribute.attributeType.name]
    ]);

    const nonRestrictedPermissionBasedIsDisabled = isDisabled || !canModifyNonRestrictedAttributeProperties;
    const restrictedPermissionBasedIsDisabled = isDisabled || !canModifyAttributeProperties;

    const selectedOption: SelectOption = useMemo(() => {
        if (labelForTheming) {
            return attributeLabelOptions.find(al => al.label === labelForTheming);
        } else if (attribute.attributeLabel) {
            return attributeLabelOptions.find(al => al.value === attribute.attributeLabel.id);
        }

        return null;
    }, [labelForTheming, attribute.attributeLabel, attributeLabelOptions]);

    const isAttributeLabelDisabled = useMemo(() => {
        return isDisabled || (isUsingThemedUI && localizeStrings && !mergeThemingAndLocalizedFile) || !canModifyBranding;
    }, [isDisabled, isUsingThemedUI, localizeStrings, mergeThemingAndLocalizedFile, canModifyBranding]);

    const attributeTabResources = resources.pages.verificationServiceDetails.tabs.attributes;
    return (
        <div className={styles.sortableAttributeContainer}>
            <div className={styles.headerContent}>
                <span className={styles.attributeLabelInfo}>
                    <FontAwesomeIcon className={styles.listIcon} icon={faListOl} />{index + 1}. {attribute.name}
                </span>
                <SelectFormGroup
                    controlId={`attribute-${attribute.attributeType?.id}-label-select`}
                    automationId={`attribute-${attribute.attributeType?.id}-label-select`}
                    className={styles.selectFormGroup}
                    ariaLabel={resources.common.inputs.attributeLabel.label}
                    label={resources.common.inputs.attributeLabel.label}
                    disabled={isAttributeLabelDisabled}
                    isClearable={false}
                    isRequired={false}
                    isSearchable={false}
                    labelWidth={LabelWidthTypes.EXTRA_SMALL}
                    onChange={onChangeAttributeLabel}
                    options={attributeLabelOptions}
                    selectedOption={selectedOption}
                />
                {!isAttributeLabelDisabled && (
                    <Button id={`attribute-${attribute.attributeType?.id}-add-attribute-label`} onClick={onClickAddAttributeLabel} className={styles.addAttributeLabelButton} variant={"primary"}>
                        {attributeTabResources.buttons.addLabel.label}
                    </Button>
                )}
                {(canViewAttributeProperties || canViewNonRestrictedAttributeProperties) && (
                    <div className={styles.expandIconContainer} onClick={toggleSetIsExpanded}>
                        <FontAwesomeIcon
                            id={`attribute-${attribute.attributeType?.id}-show-advanced-icon`}
                            title={"Show Advanced"}
                            className={`${styles.chevron} ${!isExpanded && styles.rotate}`}
                            icon={faChevronCircleUp}
                        />
                    </div>
                )}

                {!isDisabled && (
                    <div className={styles.marginLeftAuto}>
                        <ClickableIcon controlId={`attribute-${attribute.attributeType?.id}-remove-attribute`} icon={faTrash} onClick={handleRemoveAttribute} className={styles.afterInputIcon} />
                    </div>
                )}
            </div>
            {isExpanded && (canViewNonRestrictedAttributeProperties || canViewAttributeProperties) && (
                <div className={styles.advancedContent}>
                    {attributePropertyRows.map((apr, row) => (
                        <div key={`non-restricted-attribute-${attribute.id}-property-row-${index}-${row}`} className={styles.contentRow}>
                            {apr.properties.map(attributeProperty => (
                                <AttributePropertyRow
                                    key={`attribute-${attribute.id}-property-${attributeProperty.name}`}
                                    attribute={attribute}
                                    attributeProperty={attributeProperty}
                                    handleCustomTooltipChange={handleCustomTooltipChange}
                                    updateAttributeProperty={updateAttributeProperty}
                                    isDisabled={
                                        NON_RESTRICTED_ATTRIBUTE_PROPERTIES.includes(attributeProperty.name)
                                            ? nonRestrictedPermissionBasedIsDisabled
                                            : restrictedPermissionBasedIsDisabled
                                    }
                                />
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};